/**
 * TanStack Query hooks for the M11 settings, import, and export
 * endpoints.
 *
 * Reference: spec sections 6.8, 6.9, 15.11.
 */

import { useAuth } from '@clerk/clerk-react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SettingsPatch, SettingsState } from '@workspace/shared';

import { apiRequest } from '../api-client';

/**
 * Base URL for direct fetch calls (multipart upload + binary download).
 * Mirrors `apiRequest`'s use of `VITE_API_URL` so these endpoints hit
 * the API origin rather than the Cloudflare Pages frontend origin,
 * where POST/GET on `/api/v1/...` fall back to the SPA HTML and the
 * static-asset 405 response.
 */
const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export const SETTINGS_KEY = ['settings'] as const;

function useTokenGetter() {
  const { getToken } = useAuth();
  return async (): Promise<string> => {
    const token = await getToken();
    if (!token) throw new Error('No active Clerk session');
    return token;
  };
}

export function useSettings(): UseQueryResult<SettingsState, Error> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async () => {
      const token = await tokenGetter();
      return apiRequest<SettingsState>('/api/v1/settings', { token });
    },
  });
}

export function useUpdateSettings(): UseMutationResult<SettingsState, Error, SettingsPatch> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch) => {
      const token = await tokenGetter();
      return apiRequest<SettingsState>('/api/v1/settings', {
        method: 'PATCH',
        body: patch,
        token,
      });
    },
    onSuccess: (data) => {
      qc.setQueryData(SETTINGS_KEY, data);
    },
  });
}

interface ImportResult {
  imported: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

/**
 * Upload a Monday-shaped xlsx / CSV file to the leads import endpoint.
 * Returns the import result envelope.
 */
export function useImportMondayLeads(): UseMutationResult<ImportResult, Error, File> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file) =>
      uploadFile('/api/v1/import/monday/leads', file, await tokenGetter()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sales', 'leads'] });
    },
  });
}

export function useImportMondayOpportunities(): UseMutationResult<ImportResult, Error, File> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file) =>
      uploadFile('/api/v1/import/monday/opportunities', file, await tokenGetter()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sales', 'opportunities'] });
    },
  });
}

async function uploadFile(path: string, file: File, token: string): Promise<ImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  // Direct API origin — relative paths land on the Cloudflare Pages
  // static SPA and return 405 (UAT 3.3-3.8, 4.1-4.3).
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    body: fd,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Import failed: ${res.status} ${text}`);
  }
  return res.json();
}

/**
 * Build a token-authenticated download URL for the export endpoints.
 * Used by the Settings UI's Export buttons (anchors with a target).
 */
export async function downloadExport(path: string, filename: string, token: string): Promise<void> {
  // Direct API origin so the response body is the actual CSV / JSON.
  // Relative paths resolve to the Cloudflare Pages frontend and return
  // the SPA HTML body — the browser then writes that to `tasks.csv`
  // which Numbers refuses to open (UAT 10.1-10.6).
  const res = await fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
